import { useState, useEffect, useCallback } from "react";
import SimulationDat from "../Data/SimulationDat";
import moment from "moment";
import { TimeLeft } from "../Components";

const validStages = new Set([
  "Cache",
  "Capture",
  "Assassination",
  "Drone",
  "Rescue",
]);

const RollingGame = () => {
  const [tentA, setTentA] = useState([]);
  const [tentB, setTentB] = useState([]);
  const [tentC, setTentC] = useState([]);
  const [konzuTent, setKonzuTent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rotationEndTime, setRotationEndTime] = useState(null);
  const [hasRetried, setHasRetried] = useState(false);
  const [showStages, setShowStages] = useState(false);
  const [rollCounter, setRollCounter] = useState(
    Number(localStorage.getItem("currentRollCount")) || 0
  );
  // eslint-disable-next-line no-unused-vars
  const [rollHistory, setRollHistory] = useState(
    JSON.parse(localStorage.getItem("rollHistory")) || []
  );
  const [isLocked, setIsLocked] = useState(() => {
    localStorage.setItem("isLocked", "false");
    return false;
  });

  useEffect(() => {
    setIsLocked(false);
    setRollCounter(0);

    localStorage.setItem("isLocked", "false");
    localStorage.setItem("currentRollCount", "0");
  }, []);

  const randomizeBounties = (bounties) => {
    if (!Array.isArray(bounties)) return [];
    return bounties
      .map((job) => {
        const bountyData = Object.values(SimulationDat).find(
          (b) => b.name.toLowerCase() === job.toLowerCase() || b.path === job
        );
        if (!bountyData) return null;
        const initialStages = [...bountyData.stages.initial];
        const randomInitial = initialStages
          .sort(() => Math.random() - 0.5)
          .slice(0, 4);
        const finalStages = [...bountyData.stages.final];
        const randomFinal =
          finalStages.length > 0
            ? [finalStages[Math.floor(Math.random() * finalStages.length)]]
            : [];
        return {
          name: bountyData.name,
          stages: [...randomInitial, ...randomFinal],
        };
      })
      .filter(Boolean);
  };

  const fetchBounties = useCallback(() => {
    setLoading(true);

    fetch("https://oracle.browse.wf/location-bounties")
      .then((response) => response.json())
      .then((data) => {
        const cetusBounties = data.CetusSyndicate || {};
        setTentA(randomizeBounties(cetusBounties["TentA"] || []));
        setTentB(randomizeBounties(cetusBounties["TentB"] || []));
        setTentC(randomizeBounties(cetusBounties["TentC"] || []));
      })
      .catch(() => {
        setTentA([]);
        setTentB([]);
        setTentC([]);
      });

    fetch("https://api.warframestat.us/pc/?language=en")
      .then((response) => response.json())
      .then((data) => {
        const ostronBounties = data.syndicateMissions?.find(
          (mission) => mission.syndicate === "Ostrons"
        );

        const konzuBounties = ostronBounties
          ? randomizeBounties(
              ostronBounties.jobs
                ?.filter(
                  (job) =>
                    job.enemyLevels?.[0] === 40 && job.enemyLevels?.[1] === 60
                )
                .map((job) => job.type) || []
            )
          : [];

        setKonzuTent(konzuBounties);
        setLoading(false);
        setRotationEndTime(moment(data.syndicateMissions?.[0]?.expiry));
      })
      .catch(() => {
        setKonzuTent([]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchBounties();
  }, [fetchBounties]);

  const resetRollsForNewRotation = useCallback(() => {
    setRollHistory((prev) => {
      localStorage.setItem("rollHistory", JSON.stringify(prev));
    });

    setRollCounter(0);
    setIsLocked(false);
    localStorage.setItem("currentRollCount", "0");
    localStorage.setItem("isLocked", "false");
  }, []);

  useEffect(() => {
    if (rotationEndTime) {
      const checkRotation = setInterval(() => {
        if (moment(rotationEndTime).diff(moment()) <= 0) {
          resetRollsForNewRotation();
          fetchBounties();
        }
      }, 20000);
      return () => clearInterval(checkRotation);
    }
  }, [rotationEndTime, fetchBounties, resetRollsForNewRotation]);

  const handleRetry = () => {
    if (hasRetried) return;
    setRollCounter(0);
    setShowStages(false);
    localStorage.setItem("currentRollCount", "0");
    setIsLocked(false);
    localStorage.setItem("isLocked", "false");
    setHasRetried(true);
  };

  const handleRandomizeClick = () => {
    if (isLocked) return;

    setRollCounter((prev) => {
      const newCount = prev + 1;
      localStorage.setItem("currentRollCount", newCount);
      return newCount;
    });
    setShowStages(true);
    const newTentA = randomizeBounties(tentA.map((b) => b.name));
    const newTentB = randomizeBounties(tentB.map((b) => b.name));
    const newTentC = randomizeBounties(tentC.map((b) => b.name));
    const newKonzuTent = randomizeBounties(konzuTent.map((b) => b.name));

    setTentA(newTentA);
    setTentB(newTentB);
    setTentC(newTentC);
    setKonzuTent(newKonzuTent);
    console.log("newTentA", newTentA);
    console.log("newTentB", newTentB);
    console.log("newTentC", newTentC);
    console.log("newKonzuTent", newKonzuTent);

    const allBounties = [
      ...newTentA,
      ...newTentB,
      ...newTentC,
      ...newKonzuTent,
    ];

    const foundValidBounty = allBounties.some((bounty) =>
      bounty.stages.every((stage) => validStages.has(stage))
    );

    if (foundValidBounty) {
      setRollHistory((prev) => {
        const newEntry = {
          rollNumber: rollCounter + 1,
          timestamp: new Date().toISOString(), // UTC timestamp
        };
        const updatedHistory = [...prev, newEntry];
        localStorage.setItem("rollHistory", JSON.stringify(updatedHistory));
        return updatedHistory;
      });

      setIsLocked(true);
      localStorage.setItem("isLocked", "true");

      setHasRetried(false);
    }
  };

  if (loading) return <p>Loading bounties...</p>;

  const allTents = [
    { name: "Konzu Tent", bounties: konzuTent },
    { name: "Tent A", bounties: tentA },
    { name: "Tent B", bounties: tentB },
    { name: "Tent C", bounties: tentC },
  ];

  return (
    <div className="p-5 w-full bg-yellow-500 text-black flex flex-col justify-start text-center items-center font-pathway">
      <h1 className="font-monument text-6xl p-0">SKINNIX</h1>
      <h1 className="font-monument text-6xl p-0">EXTENDED</h1>
      <h1 className="font-monument text-6xl p-0">EXTRABOLD</h1>
      <h2 className="text-2xl font-semibold font-monument">
        Next Rotation In : <TimeLeft />
      </h2>
      <p className="text-2xl font-semibold font-monument">
        ROlls this rotation: {rollCounter}
      </p>
      <div className="flex flex-row gap-5 justify-center items-center">
        <button
          onClick={handleRandomizeClick}
          className={`px-4 py-2 rounded-lg text-black font-bold ${
            isLocked ? "bg-green-500 cursor-not-allowed" : "bg-yellow-500"
          }`}
          disabled={isLocked}
        >
          {isLocked ? "FOUND" : "Roll Stages"}
        </button>
        <button
          className="px-4 py-2 font-bold bg-green-400 rounded-lg"
          onClick={handleRetry}
        >
          RETRY
        </button>
      </div>
      {isLocked && (
        <p className="text-xl text-red-600">Try again next rotation</p>
      )}
      <div>
        {allTents.map(({ name, bounties }) => (
          <div key={name} className="bg-blue-300 p-2">
            <h2 className="text-2xl font-semibold">{name}</h2>
            {Array.isArray(bounties) && bounties.length > 0 ? (
              <div className="p-2 text-black">
                {bounties.map((bounty, index) => {
                  const allStagesInSet = bounty.stages.every((stage) =>
                    validStages.has(stage)
                  );
                  const boxColor =
                    allStagesInSet && showStages
                      ? "bg-green-300"
                      : "bg-red-300";

                  return (
                    <div key={index} className="text-xl font-bold">
                      {bounty.name}
                      <div className="flex flex-row gap-2 justify-between items-center">
                        {bounty.stages.map((stage, stageIndex) => (
                          <div
                            key={stageIndex}
                            className={`px-2 py-1 rounded ${boxColor}`}
                          >
                            {showStages ? stage : "?"}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-red-300 p-2 text-xl font-bold">
                No bounty in {name}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RollingGame;
